import { html, nothing, unsafeCSS } from 'lit';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { customElement, state, property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { ApiService } from '@trp-dms-digital-shared/ds-ui-library-api';
import { HoldingsFragments } from '../../common/fragments/holdings.fragments';
import { PopApiBaseComponent } from '../../base/pop-api-base-component';
import type { Holdings } from './holdings.model';
import { Constants, HOLDINGS_OPTIONS } from './holdings.constants';
import { Constants as BaseConstants } from '../../base/base.constants.js';
import { sanitizeHTML } from '../../common/utils/sanitizeHTML';
import {
  dateShort,
  dateStringSplit,
  dateToMMDDYYYY,
  dateToYYYYMMDD,
} from '../../common/utils/formatDate';
import { generateLocalDisclaimerText } from '../../common/utils/generateDisclaimer';
import type { Disclaimer } from '../../common/types/disclaimer.model';
import { AssetClassEnum } from '../../common/enums/asset-class';
import style from './holdings.scss?inline';
import { isNullish } from '../../common/utils/nullish';
import { SortDirection, SortDirections } from '../../common/utils/tableSort';
import {
  compareTableDataAscending,
  compareTableDataDescending,
  getColumnsToShow,
  getHoldingsMatchingQuery,
  getSortedHoldings,
} from './holdings.utils';

/**
 * @tag pop-holdings-component
 * @summary Represents a component that displays the top ten holdings of a product.
 * @attribute {Holdings} [data-component-object] holdings object
 */
@customElement(Constants.TAG_NAME)
export class PopHoldingsComponent extends PopApiBaseComponent {
  static get styles() {
    return [...super.styles, unsafeCSS(style)];
  }

  componentObject?: Holdings;

  /**
   * @internal
   */
  tagName = Constants.TAG_NAME;

  /**
   * @internal
   */
  fragment = HoldingsFragments.holdingsFragment;

  /**
   * @internal
   */
  @state()
  holdingsOption: string = Constants.DEFAULT_HOLDINGS_OPTION;

  /**
   * @internal
   * This keeps track of the current page to show in the full holdings table
   */
  @state()
  fullHoldingsCurrentPage = 1;

  /**
   * @internal
   * This stores the topTenHoldings dropdown date value
   */
  @state()
  topTenHoldingSelectedAsOfDate: string | undefined = '';

  /**
   * @internal
   * This stores the fullHoldings dropdown date value
   */
  @state()
  fullHoldingSelectedAsOfDate: string | undefined = '';

  /**
   * @internal
   * This stores the search query entered by the user
   */
  @state()
  searchQuery = '';

  /**
   * @internal
   * This stores the column and direction that the full holdings are sorted by
   */
  @state()
  fullHoldingsSortState: { column: string; direction: SortDirection } = {
    column: Constants.PERCENT_OF_PORTFOLIO_COLUMN_ID,
    direction: SortDirections.DESCENDING,
  };

  /**
   * @internal
   * This stores the column and direction that the top ten holdings are sorted by
   */
  @state()
  topTenHoldingsSortState: { column: string; direction: SortDirection } = {
    column: Constants.PERCENT_OF_PORTFOLIO_COLUMN_ID,
    direction: SortDirections.DESCENDING,
  };

  /**
   * Product name
   */
  @property({
    attribute: BaseConstants.DATA_PRODUCT_NAME,
    type: String,
  })
  productName = '';

  constructor() {
    super(new ApiService());
  }

  protected processResponse(_response): void {
    this.componentObject = {
      assetClass: _response.metadata.assetTypeName,
      topTen:
        _response.topTenHoldingsExhibit &&
        _response.topTenHoldingsExhibit.length > 0
          ? _response.topTenHoldingsExhibit[0]
          : null,
      full:
        _response.fullHoldingsExhibit &&
        _response.fullHoldingsExhibit.length > 0
          ? _response.fullHoldingsExhibit[0]
          : null,
    };
  }

  private refreshData(): void {
    this.callApiService({
      historicalDates: {
        topTenHoldings: [this.topTenHoldingSelectedAsOfDate],
        fullHoldings: [this.fullHoldingSelectedAsOfDate],
      },
    });
  }

  private handleFullHoldingsAsOfDateChange(
    event: Event & { target?: { value?: string } },
  ) {
    if (event.target?.value) {
      this.fullHoldingSelectedAsOfDate = dateStringSplit(event.target?.value);
      this.refreshData();
    }
  }

  private async handleFullHoldingsPageChange(
    event: CustomEvent & { detail?: { page?: number; interaction?: boolean } },
  ) {
    const { detail } = event;

    if (detail?.page && detail?.interaction) {
      const { page, interaction } = detail;
      if (
        page !== undefined &&
        page >= 1 &&
        page <= this.fullHoldingsNumberOfPages() &&
        interaction === true
      ) {
        this.fullHoldingsCurrentPage = page;

        // In the case where the new page has less holdings than the previous page
        // and the pagination control ends up out of view, we will scroll it back
        // into view
        await this.updateComplete;

        const contentBelowTable = this.shadowRoot?.getElementById(
          Constants.CONTENT_BELOW_TABLE_ID,
        );
        const boundingClientRect = contentBelowTable?.getBoundingClientRect();

        if (boundingClientRect && boundingClientRect.bottom < 200) {
          contentBelowTable?.scrollIntoView({
            block: 'center',
          });
        }
      }
    }
  }

  protected initialize() {
    super.initialize();
    this.fullHoldingSelectedAsOfDate =
      this.componentObject?.full?.effectiveDate;
    this.topTenHoldingSelectedAsOfDate =
      this.componentObject?.topTen?.effectiveDate;
  }

  private renderAsOfDateFull() {
    const availableDates = this.componentObject?.full?.availableDates;
    return html`
      ${availableDates && availableDates.length === 1
        ? html` <span
            class="b-typography-sans-serif--caption-loose--300 b-font-weight-light"
          >
            ${Constants.DATA_AS_OF} ${dateShort(availableDates[0])}
          </span>`
        : nothing}
      ${availableDates && availableDates.length > 1
        ? html`<beacon-control>
            <label
              slot="label"
              for="${Constants.FULL_HOLDINGS_AS_OF_DATE_SELECT_ID}"
              >${Constants.DATA_AS_OF}</label
            >
            <select
              id="${Constants.FULL_HOLDINGS_AS_OF_DATE_SELECT_ID}"
              @change="${this.handleFullHoldingsAsOfDateChange}"
            >
              ${availableDates.map(
                (date) =>
                  html`<option
                    value="${date}"
                    ?selected=${date ===
                    this.componentObject?.full?.effectiveDate}
                  >
                    ${dateToMMDDYYYY(date)}
                  </option>`,
              )}
            </select>
          </beacon-control> `
        : nothing}
    `;
  }

  private searchInputEventHandler(
    event: Event & { target?: { value?: string } },
  ) {
    // Trim leading and trailing white space and replace multiple spaces with a single space
    this.searchQuery = event.target?.value?.trim().replace(/\s+/g, ' ') ?? '';
  }

  private renderFullHoldingsControls() {
    const allHoldings = this.componentObject?.full?.holdings;
    const showSearchInput = allHoldings && allHoldings.length > 0;

    return html`
      <div class="b-margin-block-end--micro--jumbo controls">
        ${this.renderAsOfDateFull()}
        ${showSearchInput
          ? html`<div class="search-control">
              <beacon-control>
                <label
                  slot="label"
                  for="${Constants.FULL_HOLDINGS_SEARCH_INPUT_ID}"
                  >${Constants.FULL_HOLDINGS_SEARCH_LABEL}</label
                >
                <input
                  type="text"
                  id="${Constants.FULL_HOLDINGS_SEARCH_INPUT_ID}"
                  @input="${this.searchInputEventHandler}"
                />
                <span class="search-icon" slot="suffix">
                  <beacon-icon name="search"></beacon-icon>
                </span>
                <div slot="hint" class="visually-hidden">
                  ${Constants.FULL_HOLDINGS_SEARCH_DESCRIPTION}
                </div>
              </beacon-control>
            </div>`
          : nothing}
      </div>
    `;
  }

  private renderNoResultsMessage() {
    const leftQuote = String.fromCharCode(8220); // Unicode for “
    const rightQuote = String.fromCharCode(8221); // Unicode for ”
    return html`<span
      class="b-typography-sans-serif--caption-loose-200 b-font-weight-light"
      >No results for ${leftQuote}<span
        class="b-typography-sans-serif--caption-loose-200 b-font-weight-bold"
        >${this.searchQuery}</span
      >${rightQuote}</span
    >`;
  }

  private handleTopTenHoldingsAsOfDateChange(
    event: Event & { target?: { value?: string } },
  ) {
    if (event.target?.value) {
      this.topTenHoldingSelectedAsOfDate = dateStringSplit(event.target?.value);
      this.refreshData();
    }
  }

  private renderTopTenHoldingsControls() {
    const availableDates = this.componentObject?.topTen?.availableDates;
    return html`
      <div class="b-margin-block-end--micro--jumbo controls">
        ${availableDates && availableDates.length === 1
          ? html` <span
              class="b-typography-sans-serif--caption-loose--300 b-font-weight-light"
            >
              ${Constants.DATA_AS_OF} ${dateShort(availableDates[0])}
            </span>`
          : nothing}
        ${availableDates && availableDates.length > 1
          ? html`
              <beacon-control>
                <label
                  slot="label"
                  for="${Constants.TOP_TEN_HOLDINGS_AS_OF_DATE_SELECT_ID}"
                  >${Constants.DATA_AS_OF}</label
                >
                <select
                  id="${Constants.TOP_TEN_HOLDINGS_AS_OF_DATE_SELECT_ID}"
                  @change="${this.handleTopTenHoldingsAsOfDateChange}"
                >
                  ${this.componentObject?.topTen?.availableDates?.map(
                    (date) =>
                      html`<option
                        value="${date}"
                        ?selected=${date ===
                        this.componentObject?.topTen?.effectiveDate}
                      >
                        ${dateToMMDDYYYY(date)}
                      </option>`,
                  )}
                </select>
              </beacon-control>
            `
          : nothing}
      </div>
    `;
  }

  private fullHoldingsNumberOfPages() {
    const preppedHoldings = this.getFilteredAndSortedFullHoldings();

    return Math.ceil(preppedHoldings.length / Constants.ROWS_PER_PAGE);
  }

  private getFullHoldingsPageToShow() {
    const numberOfPages = this.fullHoldingsNumberOfPages();
    return this.fullHoldingsCurrentPage <= numberOfPages
      ? this.fullHoldingsCurrentPage
      : numberOfPages;
  }

  private renderPagination() {
    return html`<div
      id="${Constants.CONTENT_BELOW_TABLE_ID}"
      class="content-below-table b-padding-block-start--micro--jumbo b-flex b-align-center b-justify-end"
    >
      <beacon-pagination
        variant="input"
        pages="${this.fullHoldingsNumberOfPages()}"
        current-page="${this.getFullHoldingsPageToShow()}"
        @beacon-event-change="${this.handleFullHoldingsPageChange}"
      ></beacon-pagination>
    </div>`;
  }

  private renderContentBelowFullHoldingsTable() {
    const filteredHoldings = this.getFilteredFullHoldings();

    const classes = {
      'visually-hidden': filteredHoldings.length > 0,
      'content-below-table': true,
      'b-padding-block-start--micro--jumbo': true,
      'b-flex': true,
      'b-align-center': true,
      'b-justify-center': true,
    };

    // The live region needs to persist in the DOM so we hide its content visually when not in use
    return html` <div aria-live="assertive" aria-atomic="true">
        <div class=${classMap(classes)}>
          ${filteredHoldings.length === 0
            ? this.renderNoResultsMessage()
            : nothing}
        </div>
      </div>
      ${filteredHoldings.length > 0 ? this.renderPagination() : nothing}`;
  }

  private renderHoldingsUnavailable() {
    return html`<pop-no-data-available-component
      data-message="${Constants.HOLDINGS_UNAVAILABLE}"
    ></pop-no-data-available-component>`;
  }

  private hasSearchQuery() {
    return this.searchQuery && this.searchQuery.length > 0;
  }

  private getFilteredFullHoldings() {
    const fullHoldings = this.componentObject?.full?.holdings || [];

    if (this.hasSearchQuery()) {
      return getHoldingsMatchingQuery(fullHoldings, this.searchQuery);
    }
    return fullHoldings;
  }

  /**
   * Updates the sort state for the full holdings table.
   *
   * @param column The id of the column to sort by
   * @param direction The sort direction, either ascending or descending
   */
  private setFullHoldingsSortState(column: string, direction: SortDirection) {
    this.fullHoldingsSortState = {
      column,
      direction,
    };
  }

  /**
   * Updates the sort state for the top ten holdings table
   *
   * @param column The id of the column to sort by
   * @param direction The sort direction, either ascending or descending
   */
  private setTopTenHoldingsSortState(column: string, direction: SortDirection) {
    this.topTenHoldingsSortState = {
      column,
      direction,
    };
  }

  /**
   * Processes a request to sort the full holdings table by a given column.
   *
   * @param event Custom event that is expected to have the id of the column
   * to sort by in the `detail.column` property
   */
  private handleFullHoldingsSortEvent(event: CustomEvent) {
    const sortedColumn = this.fullHoldingsSortState.column;

    // If it's the column that is already sorted, toggle the direction,
    // otherwise start with ascending
    if (sortedColumn === event.detail?.column) {
      this.setFullHoldingsSortState(
        sortedColumn,
        this.fullHoldingsSortState.direction === SortDirections.ASCENDING
          ? SortDirections.DESCENDING
          : SortDirections.ASCENDING,
      );
    } else {
      this.setFullHoldingsSortState(
        event.detail?.column,
        SortDirections.ASCENDING,
      );
    }
  }

  /**
   * Processes a request to sort the top ten holdings table by a given column.
   *
   * @param event Custom event that is expected to have the id of the column
   * to sort by in the `detail.column` property
   */
  private handleTopTenHoldingsSortEvent(event: CustomEvent) {
    const sortedColumn = this.topTenHoldingsSortState.column;

    // If it's the column that is already sorted, toggle the direction,
    // otherwise start with ascending
    if (sortedColumn === event.detail?.column) {
      this.setTopTenHoldingsSortState(
        sortedColumn,
        this.topTenHoldingsSortState.direction === SortDirections.ASCENDING
          ? SortDirections.DESCENDING
          : SortDirections.ASCENDING,
      );
    } else {
      this.setTopTenHoldingsSortState(
        event.detail?.column,
        SortDirections.ASCENDING,
      );
    }
  }

  /**
   * Maps a column id in the table to the data key to use for sorting that column
   *
   * @param column The column id from the holdings table
   * @returns The corresponding key in the holding object
   */
  private mapColumnIdToKey(column: string) {
    const matchingColumn = Constants.TABLE_COLUMNS.find(
      (tableColumn) => tableColumn.id === column,
    );

    return matchingColumn?.key ?? '';
  }

  /**
   * @returns The full holdings filtered using the optional search query
   * and in the sorted order based on the current sort state
   */
  private getFilteredAndSortedFullHoldings() {
    return getSortedHoldings(
      this.getFilteredFullHoldings(),
      this.mapColumnIdToKey(this.fullHoldingsSortState.column),
      this.fullHoldingsSortState.direction,
    );
  }

  private renderFullHoldingsTable() {
    const allFullHoldings = this.componentObject?.full?.holdings || [];

    if (allFullHoldings.length === 0) {
      return this.renderHoldingsUnavailable();
    }

    const fullHoldingsColumns = getColumnsToShow(
      Constants.TABLE_COLUMNS,
      allFullHoldings,
    );

    const preppedHoldings = this.getFilteredAndSortedFullHoldings();

    const startingRow =
      (this.getFullHoldingsPageToShow() - 1) * Constants.ROWS_PER_PAGE;
    const holdingsForTable = preppedHoldings.slice(
      startingRow,
      startingRow + Constants.ROWS_PER_PAGE,
    );
    return html`
      <pop-holdings-table-component
        data-component-object="${JSON.stringify({
          holdings: holdingsForTable,
          columnsToShow: fullHoldingsColumns,
          sortState: this.fullHoldingsSortState,
        })}"
        @pop-holdings-sort="${this.handleFullHoldingsSortEvent}"
      ></pop-holdings-table-component>

      ${this.renderContentBelowFullHoldingsTable()}
      ${this.renderDisclosures(this.componentObject?.full?.disclaimers)}
    `;
  }

  /**
   * @returns The top ten holdings in the sorted order based on the current sort state
   */
  private getSortedTopTenHoldings() {
    const topTenHoldings = this.componentObject?.topTen?.holdings;
    if (!topTenHoldings) {
      return [];
    }
    const key = this.mapColumnIdToKey(this.topTenHoldingsSortState.column);

    if (this.topTenHoldingsSortState.direction === SortDirections.ASCENDING) {
      return [...topTenHoldings].sort((a, b) => {
        return compareTableDataAscending(a, b, key);
      });
    } else {
      return [...topTenHoldings].sort((a, b) => {
        return compareTableDataDescending(a, b, key);
      });
    }
  }

  private renderTopTenHoldingsTable() {
    const topTenHoldings = this.getSortedTopTenHoldings();
    const topTenHoldingsColumns = getColumnsToShow(
      Constants.TABLE_COLUMNS,
      topTenHoldings,
    );

    if (topTenHoldings && topTenHoldings.length > 0) {
      return html`<pop-holdings-table-component
          data-component-object="${JSON.stringify({
            holdings: topTenHoldings,
            sortState: this.topTenHoldingsSortState,
            columnsToShow: topTenHoldingsColumns,
          })}"
          @pop-holdings-sort="${this.handleTopTenHoldingsSortEvent}"
        ></pop-holdings-table-component>
        ${this.renderDisclosures(this.componentObject?.topTen?.disclaimers)}`;
    }

    return this.renderHoldingsUnavailable();
  }

  private handleFullHoldingsTabSelection() {
    this.holdingsOption = HOLDINGS_OPTIONS.FULL;
  }

  private handleTopTenHoldingsTabSelection() {
    this.holdingsOption = HOLDINGS_OPTIONS.TOP_TEN;
  }

  private renderTabs() {
    return html` <beacon-tabs variant="secondary" tab-type="standard">
      <beacon-tab
        slot="tab-children"
        label="Full holdings"
        value="1"
        active
        @beacon-event-click="${this.handleFullHoldingsTabSelection}"
      ></beacon-tab>
      <beacon-tab
        slot="tab-children"
        label="Top 10 holdings"
        value="2"
        @beacon-event-click="${this.handleTopTenHoldingsTabSelection}"
      ></beacon-tab>
      <beacon-tab-panel slot="tab-panel" value="1">
        ${this.renderFullHoldingsControls()} ${this.renderFullHoldingsTable()}
      </beacon-tab-panel>
      <beacon-tab-panel slot="tab-panel" value="2">
        ${this.renderTopTenHoldingsControls()}
        ${this.renderTopTenHoldingsTable()}
      </beacon-tab-panel>
    </beacon-tabs>`;
  }

  private getSelectedTabAsText() {
    return this.holdingsOption === HOLDINGS_OPTIONS.FULL ? 'Full' : 'Top 10';
  }

  private formatFileName(
    productName: string,
    dateString: string,
    exhibit: string,
  ): string {
    const formattedDate = dateToYYYYMMDD(dateString);
    const formattedProductName = productName.toLowerCase().replace(/ /g, '-');
    return `${formattedProductName}_${formattedDate}_${exhibit}`;
  }

  private getDownloadLink(): string {
    const exhibit =
      this.holdingsOption === HOLDINGS_OPTIONS.FULL
        ? 'full-holdings.xlsx'
        : 'top10-holdings.xlsx';

    const date =
      (this.holdingsOption === HOLDINGS_OPTIONS.FULL
        ? this.fullHoldingSelectedAsOfDate
        : this.topTenHoldingSelectedAsOfDate) ?? '';

    return this.formatFileName(this.productName, date, exhibit);
  }

  private renderDisclosures(disclaimers?: Disclaimer[]) {
    if (!disclaimers || disclaimers.length === 0) {
      return nothing;
    }

    const disclaimerText = generateLocalDisclaimerText(disclaimers);

    return html`<div class="b-margin-block-start--micro--jumbo">
      <pop-disclosure-component data-footnote-indicator-type="symbol">
        ${unsafeHTML(sanitizeHTML(disclaimerText))}
      </pop-disclosure-component>
    </div>`;
  }

  private hasHoldingsData(): boolean {
    if (!this.componentObject) {
      return false;
    }

    const { full, topTen } = this.componentObject;

    if (isNullish(full) && isNullish(topTen)) {
      return false;
    }

    return true;
  }

  private renderDownloadHoldingsButton() {
    return html`
      <beacon-button
        icon-align="right"
        variant="tertiary"
        scale="mini"
        link-href="${this.getDownloadLink()}"
      >
        Download ${this.getSelectedTabAsText()} Holdings
        <beacon-icon slot="suffix" name="download"></beacon-icon>
      </beacon-button>
    `;
  }

  /**
   * Renders the top ten holdings component.
   * @returns The rendered HTML template.
   */
  render() {
    const holdingLabel =
      this.componentObject?.assetClass === AssetClassEnum.FIXED_INCOME
        ? Constants.HOLDINGS_LABEL_FIXED_INCOME
        : Constants.HOLDINGS_LABEL;

    return this.renderComponent(html`
      <div class="b-container">
        <div class="holdings-header">
          <h3
            class="holdings-title b-typography-serif--headline--100 b-font-weight-bold"
          >
            ${unsafeHTML(holdingLabel)}
          </h3>
          ${this.hasHoldingsData() ? this.renderDownloadHoldingsButton() : ''}
        </div>
        ${this.hasHoldingsData()
          ? this.renderTabs()
          : this.renderHoldingsUnavailable()}
      </div>
    `);
  }
}
